import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { AED, fmtDateTime, fmtDate } from "@/lib/api";
import { UaeMobileInput } from "@/components/AddressForm";
import AddressForm, { emptyAddress, isAddressComplete } from "@/components/AddressForm";
import { fmtBal, depositStateLabel, depositStateColor } from "@/lib/statusLabels";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { DubaiAreaSelect } from "@/components/DubaiAreaSelect";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ShieldCheck, Package, Trash2, ArrowRight, MoreHorizontal, Eye, Pause, Play } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { DepositReleaseModal } from "@/components/DepositReleaseModal";

const imgSrc = (s) => (!s ? "" : s.startsWith("http") || s.startsWith("data:") || s.startsWith("/") ? s : `data:image/jpeg;base64,${s}`);

export default function Profile() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ ...user });
  const [addr, setAddr] = useState({ ...emptyAddress, ...(user.address || {}), area: user.address?.area || user.area || "" });
  const [listings, setListings] = useState([]);
  const [txns, setTxns] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const loadListings = () => api.get("/listings", { params: { mine: true } }).then(({ data }) => setListings(data));
  useEffect(() => {
    loadListings();
    api.get("/transactions").then(({ data }) => setTxns(data)).catch(() => {});
    api.get("/auth/me").then(({ data }) => { setUser(data); setF((prev) => ({ ...data, ...prev, deposit_balance: data.deposit_balance, deposit_status: data.deposit_status, deposit_topup_required: data.deposit_topup_required, deposit_ledger: data.deposit_ledger })); }).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    if (params.get("settled") && params.get("session_id") && sessionStorage.getItem(`settled:${params.get("session_id")}`) !== "1") {
      sessionStorage.setItem(`settled:${params.get("session_id")}`, "1");
      window.history.replaceState({}, "", "/profile");
      api.get(`/payments/status/${params.get("session_id")}`).then(async () => {
        const me = await api.get("/auth/me"); setUser(me.data);
        toast.success("Balance settled — you can buy and claim items again.");
      }).catch(() => {});
    }
  }, []);

  const DRAFT_STATUSES = ["draft", "restricted_draft"];
  const drafts = listings.filter((l) => DRAFT_STATUSES.includes(l.status));
  const active = listings.filter((l) => !DRAFT_STATUSES.includes(l.status) && l.status !== "sold");
  const sold = listings.filter((l) => l.status === "sold");
  const salesMap = {};
  txns.forEach((t) => { if (t.seller_id === user.id && t.listing_id) salesMap[t.listing_id] = t; });
  const deleteDraft = async (id) => {
    try { await api.delete(`/listings/${id}`); toast.success("Draft deleted"); setConfirmDelete(null); loadListings(); }
    catch { toast.error("Could not delete draft"); }
  };
  const pauseListing = async (id) => {
    try { await api.patch(`/listings/${id}/pause`); toast.success("Listing paused — hidden from buyers"); loadListings(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not pause listing"); }
  };
  const reactivateListing = async (id) => {
    try { await api.patch(`/listings/${id}/reactivate`); toast.success("Listing is live again"); loadListings(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not reactivate listing"); }
  };
  const removeListing = async (id) => {
    try { await api.delete(`/listings/${id}`); toast.success("Listing removed"); setConfirmRemove(null); loadListings(); }
    catch { toast.error("Could not remove listing"); }
  };

  const save = async () => {
    if (!isAddressComplete(addr)) { toast.error("Please complete your address, including your area"); return; }
    try {
      const { data } = await api.put("/auth/profile", {
        name: f.name, mobile: f.mobile, address: addr,
      });
      setUser(data); toast.success("Profile updated");
    } catch { toast.error("Could not save"); }
  };

  const saveMarketing = async (val) => {
    setF((prev) => ({ ...prev, marketing_consent: val }));
    try {
      const { data } = await api.put("/auth/profile", { marketing_consent: val });
      setUser(data);
      toast.success(val ? "You'll now receive marketing emails" : "Marketing emails turned off");
    } catch {
      setF((prev) => ({ ...prev, marketing_consent: !val }));
      toast.error("Could not update preference");
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <div className="mb-6 flex items-center gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 font-heading text-2xl font-bold text-primary">{user.name?.[0]}</div>
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">{user.name}</h1>
          <p className="text-sm text-slate-500">{user.email}</p>
          {user.user_id && <p className="mt-0.5 font-mono text-xs text-slate-400" data-testid="profile-user-id">ID: {user.user_id}</p>}
        </div>
      </div>

      <DepositCard user={user} />
      <BalanceOwedCard user={user} txns={txns} />
      <BalanceHistory user={user} />
      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="payments-status-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-base font-bold text-slate-900">Payments &amp; verification</h3>
            <p className="mt-1 text-sm text-slate-600" data-testid="payments-status-line">
              Identity: <span className="font-medium text-slate-800">{user.kyc_status === "verified" ? "Verified" : (user.kyc_status === "under_review" || user.kyc_status === "rejected") ? "Pending" : "Not submitted"}</span>
              {" · "}Bank details: <span className="font-medium text-slate-800">{user.bank_details ? "Added" : "Not added"}</span>
            </p>
          </div>
          <Button data-testid="payments-open-btn" onClick={() => navigate("/payments")} className="rounded-full bg-primary font-semibold">Payments &amp; verification</Button>
        </div>
      </div>

      {user.email_verified === false && (
        <div className="mt-4 flex flex-col gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="profile-unverified-banner">
          <p className="text-sm text-amber-800">Your email isn't verified yet. Verify it to create listings, make offers and purchases.</p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-full" data-testid="profile-resend-verification" onClick={async () => { try { await api.post("/auth/resend-otp"); toast.success("Verification email sent — check your inbox."); } catch (e) { toast.error("Could not resend email"); } }}>Resend verification email</Button>
            <Button size="sm" className="rounded-full bg-primary font-semibold" onClick={() => navigate("/verify")}>Enter code</Button>
          </div>
        </div>
      )}


      <Tabs defaultValue="details">
        <TabsList className="rounded-full">
          <TabsTrigger value="details" className="rounded-full" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="listings" className="rounded-full" data-testid="tab-listings">My Listings ({active.length}) · Drafts ({drafts.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-4">
          <div className="grid gap-4 rounded-2xl border border-slate-200 p-6 sm:grid-cols-2">
            <F label="Name" required><Input value={f.name || ""} onChange={set("name")} data-testid="profile-name" className="rounded-lg" /></F>
            <F label="Mobile" required>
              <UaeMobileInput testid="profile-mobile" value={f.mobile || ""} onChange={(v) => setF((p) => ({ ...p, mobile: v }))} />
            </F>
            <div className="sm:col-span-2" data-testid="profile-address-form"><AddressForm value={addr} onChange={setAddr} /></div>
            <div className="sm:col-span-2 flex items-start justify-between gap-4 rounded-xl border border-slate-200 p-4" data-testid="profile-marketing-row">
              <div>
                <p className="text-sm font-medium text-slate-800">Marketing emails</p>
                <p className="mt-0.5 text-xs text-slate-500">Email me when items I might like are listed, plus occasional updates from SellStuff.</p>
              </div>
              <Switch data-testid="profile-marketing-toggle" checked={!!f.marketing_consent} onCheckedChange={saveMarketing} />
            </div>
            <div className="sm:col-span-2"><Button onClick={save} data-testid="profile-save" className="rounded-full bg-primary font-semibold">Save changes</Button></div>
          </div>
        </TabsContent>

        <TabsContent value="listings" className="mt-4 space-y-6">
          {/* My Drafts — above My Listings */}
          <div data-testid="my-drafts-section">
            <h3 className="mb-2 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">My Drafts ({drafts.length})</h3>
            {drafts.length === 0 ? (
              <div data-testid="drafts-empty" className="rounded-2xl border border-dashed border-slate-300 py-10 text-center text-sm text-slate-500">No drafts yet. Start listing an item and save it for later.</div>
            ) : (
              <div className="space-y-3">
                {drafts.map((l) => {
                  const cover = l.ai_thumbnail || (l.seller_photos || [])[0] || "";
                  return (
                    <div key={l.id} className="flex items-center gap-3 rounded-2xl border border-slate-200 p-4" data-testid="draft-row">
                      {cover ? <img src={imgSrc(cover)} alt="" className="h-14 w-14 rounded-xl object-cover" /> : <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-slate-100 text-slate-400"><Package className="h-6 w-6" /></div>}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2"><p className="truncate font-medium text-slate-900" data-testid="draft-title">{l.title || l.item_name || "Untitled item"}</p>
                          {l.status === "restricted_draft" ? <Badge className="rounded-full bg-orange-100 text-orange-700" data-testid="draft-badge-restricted">Restricted</Badge>
                            : l.status === "pending_review" ? <Badge className="rounded-full bg-blue-100 text-blue-700" data-testid="draft-badge-review">Under review</Badge>
                            : <Badge className="rounded-full bg-slate-100 text-slate-700" data-testid="draft-badge">Draft</Badge>}
                        </div>
                        <p className="mt-0.5 text-xs text-slate-500" data-testid="draft-saved">{savedAgo(l.created_at)}</p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <Button size="sm" onClick={() => navigate(`/sell?draft=${l.id}`)} data-testid="finish-listing-btn" className="rounded-full bg-primary font-semibold"><ArrowRight className="mr-1 h-4 w-4" /> Finish listing</Button>
                        <Button size="sm" variant="outline" onClick={() => setConfirmDelete(l.id)} data-testid="delete-draft-btn" className="rounded-full border-red-200 text-red-600 hover:bg-red-50" aria-label="Delete draft"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* My Listings */}
          <div>
            <h3 className="mb-2 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">My Listings ({active.length})</h3>
            {active.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 py-16 text-center text-slate-500">No listings yet. <Link to="/sell" className="font-semibold text-primary">Sell something</Link></div>
            ) : (
              <div className="space-y-3">
                {active.map((l) => (
                  <div key={l.id} className="flex items-center gap-3 rounded-2xl border border-slate-200 p-4" data-testid="my-listing-row">
                    {l.ai_thumbnail ? (
                      <img src={imgSrc(l.ai_thumbnail)} alt="" className="h-14 w-14 rounded-xl object-cover" />
                    ) : (
                      <div data-testid="listing-thumb-placeholder" className="flex h-14 w-14 items-center justify-center rounded-xl bg-slate-100 text-slate-400"><Package className="h-6 w-6" /></div>
                    )}
                    <Link to={`/listing/${l.id}`} className="min-w-0 flex-1">
                      <p className="truncate font-medium text-slate-900 hover:underline">{l.title}</p>
                      <p className="text-xs text-slate-500" data-testid="listing-price-line">{l.listing_type === "free" ? "Free listing" : `You receive ${AED(l.seller_desired_payout)} · Your minimum ${AED(l.seller_floor_payout)}`}</p>
                      {l.status === "declined" && l.decline_reason && <p className="mt-0.5 text-xs text-red-700" data-testid="listing-decline-reason">Reason: {l.decline_reason}</p>}
                    </Link>
                    <Badge className={`rounded-full ${l.status === "pending_review" ? "bg-blue-100 text-blue-700" : l.status === "declined" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}`} data-testid={l.status === "pending_review" ? "listing-status-under-review" : l.status === "declined" ? "listing-status-declined" : "listing-status"}>{l.status === "pending_review" ? "Under review" : l.status === "declined" ? "Declined" : titleStatus(l.status)}</Badge>
                    {l.status === "declined" ? <Button variant="outline" size="sm" className="rounded-full" data-testid="declined-view-btn" onClick={() => navigate(`/listing/${l.id}`)}>View</Button> : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="rounded-full" data-testid="manage-listing-btn">Manage <MoreHorizontal className="ml-1 h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem data-testid="manage-view" onClick={() => navigate(`/listing/${l.id}`)}><Eye className="mr-2 h-4 w-4" /> View</DropdownMenuItem>
                        {l.status === "paused" ? (
                          <DropdownMenuItem data-testid="manage-reactivate" onClick={() => reactivateListing(l.id)}><Play className="mr-2 h-4 w-4" /> Reactivate</DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem data-testid="manage-pause" onClick={() => pauseListing(l.id)}><Pause className="mr-2 h-4 w-4" /> Pause</DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem data-testid="manage-delete" className="text-red-600 focus:text-red-600" onClick={() => setConfirmRemove(l.id)}><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>)}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sold */}
          <div data-testid="sold-section">
            <h3 className="mb-2 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">Sold ({sold.length})</h3>
            {sold.length === 0 ? (
              <div data-testid="sold-empty" className="rounded-2xl border border-dashed border-slate-300 py-10 text-center text-sm text-slate-500">No sold items yet</div>
            ) : (
              <div className="space-y-3">
                {sold.map((l) => {
                  const cover = l.ai_thumbnail || (l.seller_photos || [])[0] || "";
                  const sale = salesMap[l.id];
                  return (
                    <div key={l.id} className="flex items-center gap-3 rounded-2xl border border-slate-200 p-4" data-testid="sold-listing-row">
                      {cover ? <img src={imgSrc(cover)} alt="" className="h-14 w-14 rounded-xl object-cover" /> : <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-slate-100 text-slate-400"><Package className="h-6 w-6" /></div>}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="truncate font-medium text-slate-900" data-testid="sold-title">{l.title}</p>
                          <Badge className="rounded-full bg-slate-200 text-slate-600 hover:bg-slate-200" data-testid="sold-badge">Sold</Badge>
                        </div>
                        <p className="mt-0.5 text-xs text-slate-500" data-testid="sold-price-line">
                          {sale ? `Sold for ${AED(sale.agreed_price)}` : `Listed ${AED(l.listing_price)}`}{sale?.created_at ? ` · ${fmtDateTime(sale.created_at)}` : ""}
                        </p>
                      </div>
                      <Button size="sm" variant="outline" className="shrink-0 rounded-full" data-testid="sold-view-btn" onClick={() => navigate(`/listing/${l.id}`)}><Eye className="mr-1 h-4 w-4" /> View</Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Delete this draft?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button className="rounded-full bg-red-600 hover:bg-red-700" data-testid="confirm-delete-draft" onClick={() => deleteDraft(confirmDelete)}>Delete draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmRemove} onOpenChange={(o) => !o && setConfirmRemove(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Delete this listing?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">It will be removed from the marketplace. This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setConfirmRemove(null)}>Cancel</Button>
            <Button className="rounded-full bg-red-600 hover:bg-red-700" data-testid="confirm-remove-listing" onClick={() => removeListing(confirmRemove)}>Delete listing</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
const F = ({ label, children, required }) => (<div><Label className="mb-1 block">{label}{required && <span className="text-red-500"> *</span>}</Label>{children}</div>);
function savedAgo(iso) {
  if (!iso) return "Saved recently";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "Saved today";
  if (days === 1) return "Saved 1 day ago";
  return `Saved ${days} days ago`;
}
function titleStatus(s) {
  const map = { active: "Active", under_offer: "Under Offer", locked: "Under Offer", sold: "Sold", paused: "Paused", draft: "Draft", restricted_draft: "Restricted", pending_review: "Under Review", removed: "Removed" };
  return map[s] || (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
}


function BalanceHistory({ user }) {
  const led = user.balance_owed_ledger || [];
  const items = led.map((e, i) => {
    if (!(e.amount > 0)) return null;
    const settled = led.slice(i + 1).find((x) => x.amount < 0 && (x.balance_after || 0) <= 0);
    const ref = e.order_ref ? `CR-${String(e.order_ref).slice(0, 8).toUpperCase()}` : ((e.description || "").match(/order ([A-Z0-9]{8})/)?.[1] ? `CR-${e.description.match(/order ([A-Z0-9]{8})/)[1]}` : "");
    return { key: i, text: `AED ${e.amount} owed${ref ? ` · ${ref}` : ""} · ${settled ? `settled ${fmtDateTime(settled.date)}${/deposit/i.test(settled.description || "") ? " (from deposit)" : ""}` : "outstanding"}` };
  }).filter(Boolean).reverse();
  if (!items.length) return null;
  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white p-3" data-testid="balance-history">
      <p className="mb-1 text-xs font-semibold text-slate-700">Balance history</p>
      {items.slice(0, 5).map((it) => <p key={it.key} className="text-xs text-slate-500" data-testid="balance-history-row">{it.text}</p>)}
    </div>
  );
}

function BalanceOwedCard({ user, txns }) {
  const [busy, setBusy] = useState(false);
  const owed = Math.round((user.balance_owed || 0) * 100) / 100;
  if (owed <= 0) return null;
  const last = (user.balance_owed_ledger || []).filter((e) => e.amount > 0).slice(-1)[0];
  const refId = last?.order_ref || "";
  const shortRef = refId ? refId.slice(0, 8).toUpperCase() : (last?.description || "").match(/order ([A-Z0-9]{8})/)?.[1];
  const txn = (txns || []).find((t) => (refId && t.id === refId) || (shortRef && t.id.slice(0, 8).toUpperCase() === shortRef));
  const settle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/settle-balance", { origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start payment"); setBusy(false); }
  };
  return (
    <div className="mb-6 rounded-2xl border-2 border-red-300 bg-red-50 p-5" data-testid="balance-owed-card">
      <h3 className="font-heading text-base font-bold text-red-800">Outstanding balance</h3>
      <p className="mt-1 text-sm text-red-800">You owe <b data-testid="balance-owed-amount">AED {owed}</b> to cover the delivery and return of {txn ? <>your <b data-testid="balance-owed-item">{txn.listing_title}</b></> : "an order"}{shortRef ? <> (<span data-testid="balance-owed-ref">CR-{shortRef}</span>)</> : ""}, which couldn't be delivered. You can't buy or claim items until this is settled.</p>
      <Button data-testid="settle-balance-btn" disabled={busy} onClick={settle} className="mt-3 rounded-full bg-red-600 font-semibold hover:bg-red-700">{busy ? "Starting…" : "Settle balance"}</Button>
    </div>
  );
}

function DepositCard({ user }) {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [showAllLedger, setShowAllLedger] = useState(false);
  const [confirmRelease, setConfirmRelease] = useState(false);
  const status = user.deposit_status || "none";
  const balance = user.deposit_balance || 0;
  const refreshMe = async () => { const me = await api.get("/auth/me"); setUser(me.data); };
  if (status === "none") {
    return (
      <div className="mb-6 flex flex-wrap items-center gap-2 rounded-xl bg-slate-50 p-3 text-sm text-slate-600" data-testid="deposit-card">
        <ShieldCheck className="h-4 w-4" /> No security deposit yet. You'll be asked for a refundable AED 99 deposit when you publish your first listing.
        <button data-testid="deposit-pay-btn" className="font-semibold text-primary hover:underline" onClick={() => navigate("/deposit/topup")}>Pay deposit now</button>
      </div>
    );
  }
  if (user.deposit_refund_status === "requested") {
    return <div className="mb-6 flex items-center gap-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-700" data-testid="deposit-card"><ShieldCheck className="h-4 w-4" /> Refund requested — our team is reviewing it.</div>;
  }
  if (user.deposit_refund_status === "processing") {
    return <div className="mb-6 flex items-center gap-2 rounded-xl bg-blue-50 p-3 text-sm text-blue-800" data-testid="deposit-card"><ShieldCheck className="h-4 w-4" /> Refund processing — <span className={balance < 0 ? "font-semibold text-red-600" : "font-semibold"} data-testid="deposit-balance">{fmtBal(balance)}</span> is on its way to your bank account (3 to 5 business days).</div>;
  }
  if (status === "released") {
    return <div className="mb-6 flex items-center gap-2 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700" data-testid="deposit-card"><ShieldCheck className="h-4 w-4" /> {user.deposit_refunded_at ? `Refunded on ${fmtDate(user.deposit_refunded_at)}.` : "Security deposit released."}</div>;
  }
  return (
    <>
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-sm text-blue-900" data-testid="deposit-card">
      <ShieldCheck className="h-4 w-4" />
      <span>Security deposit: <b data-testid="deposit-balance" className={balance < 0 ? "text-red-600" : ""}>{fmtBal(balance)}</b></span>
      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${depositStateColor(user)}`} data-testid="deposit-status-label">{depositStateLabel(user)}</span>
      {balance < 99 && <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700" data-testid="deposit-topup-amount">Top up AED {Math.round((99 - balance) * 100) / 100}</span>}
      <button data-testid="deposit-topup-btn" className="font-semibold text-primary hover:underline" onClick={() => navigate("/deposit/topup")}>Top up</button>
      {balance > 0 && !user.deposit_release_auto && <button data-testid="deposit-release-btn" className="text-slate-500 hover:underline" onClick={() => setConfirmRelease(true)}>· Request release</button>}
      {user.deposit_release_auto && <span className="text-xs text-slate-600" data-testid="deposit-release-queued">· Refund will be requested when your orders finish</span>}
    </div>
    <DepositReleaseModal open={confirmRelease} onClose={() => setConfirmRelease(false)} onDone={refreshMe} />
    {Array.isArray(user.deposit_ledger) && user.deposit_ledger.length > 0 && (() => {
      const all = user.deposit_ledger.slice().reverse();
      const rows = showAllLedger ? all : all.slice(0, 5);
      return (
      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-3" data-testid="deposit-ledger">
        <p className="mb-2 text-xs font-semibold text-slate-700">Deposit history</p>
        <div className="space-y-1">
          {rows.map((e, i) => (
            <div key={i} className="flex items-center justify-between gap-2 text-xs text-slate-500" data-testid="deposit-ledger-row">
              <span>{fmtDateTime(e.date)} · {e.description}{e.order_ref ? ` · CR-${String(e.order_ref).slice(0, 8).toUpperCase()}` : ""}</span>
              <span className={e.amount < 0 ? "text-red-600" : "text-emerald-700"}>{e.amount < 0 ? "−" : "+"}AED {Math.abs(e.amount)} <span className="text-slate-400">(bal {fmtBal(e.balance_after)})</span></span>
            </div>
          ))}
        </div>
        {all.length > 5 && (
          <button data-testid="deposit-ledger-toggle" onClick={() => setShowAllLedger((v) => !v)} className="mt-2 text-xs font-semibold text-primary hover:underline">
            {showAllLedger ? "Show less" : `Show all ${all.length}`}
          </button>
        )}
      </div>
      );
    })()}
    </>
  );
}
